import { Ref, ref } from 'vue'
import { DiscountRouteName } from '@invivodf/common/src/contexts/discount'
import { LogisticRouteName } from '@invivodf/common/src/contexts/logistic-offer/routes/logistic-route-name'
import { BRAND_MANAGE_SCOPES } from '@invivodf/front-seller-auth/src/BRAND_MANAGE_SCOPES'
import { SCA_MANAGE_SCOPES } from '@invivodf/front-seller-auth/src/SCA_MANAGE_SCOPES'
import IRouterNames from '@/retail/application/types/config/router'
import { useAuthentication } from '@/retail/contexts/user/application/composables/useAuthentication'

// Retail navigation links — extracted from the inline definition that used to
// live in VLayoutDefault.vue. Kept separate from useNavigationLinks (b2ag): the
// two have distinct semantics (this one carries an `allowed` flag resolved from
// retail scopes / clients permissions).
export interface IRetailNavigationLink {
  icon: string
  label: string
  link?: { name: string }
  subMenu?: {
    label: string
    link: { name: string }
  }[]
  allowed: boolean
}

export function useRetailNavigationLinks() {
  const { getClientsPermissions, getUserScopes } = useAuthentication()
  const allowedNavigationLinks: Ref<IRetailNavigationLink[]> = ref([])

  const loadRetailNavigationLinks = async (): Promise<void> => {
    const [clientsPermissions, scopes] = await Promise.all([getClientsPermissions(), getUserScopes()])
    const canReadClients = clientsPermissions.canRead
    const hasRedirectionsScope = BRAND_MANAGE_SCOPES.some((scope) => scopes.includes(scope))
    const hasScaScope = SCA_MANAGE_SCOPES.some((scope) => scopes.includes(scope))

    const navigationLinks: IRetailNavigationLink[] = [
      {
        icon: 'promotion-outlined',
        label: 'Promotions',
        allowed: true,
        link: { name: DiscountRouteName.LISTING },
      },
      {
        icon: 'truck-outlined',
        label: 'Offres logistiques',
        link: { name: LogisticRouteName.LIST },
        allowed: true,
      },
      {
        icon: 'profile-outlined',
        label: 'Clients',
        link: { name: IRouterNames.CLIENTS },
        allowed: canReadClients,
      },
      {
        icon: 'cart-bag-outlined',
        label: 'Commandes',
        allowed: true,
      },
      {
        icon: 'scale-outlined',
        label: 'Mangopay SCA',
        link: { name: IRouterNames.SCA_SELLERS },
        allowed: hasScaScope,
      },
      {
        icon: 'plus',
        label: 'Développeurs',
        subMenu: [
          {
            label: 'Webhooks',
            link: { name: 'webhooks' },
          },
        ],
        allowed: true,
      },
      {
        icon: 'switch',
        label: 'Redirections',
        link: { name: IRouterNames.BRAND_REDIRECTIONS },
        allowed: hasRedirectionsScope,
      },
      {
        icon: 'gear-outlined',
        label: 'Paramètres',
        link: { name: 'parameters' },
        allowed: true,
      },
    ]

    allowedNavigationLinks.value = navigationLinks.filter((link) => link.allowed)
  }

  return { allowedNavigationLinks, loadRetailNavigationLinks }
}
